import type { AppearanceAccent, AppearanceColorMode, WorkspaceAppearance } from "./api";

export const APPEARANCE_MODES = ["system", "light", "dark"] as const satisfies readonly AppearanceColorMode[];
export const APPEARANCE_ACCENTS = ["indigo", "blue", "teal", "green", "orange", "rose", "neutral"] as const satisfies readonly AppearanceAccent[];

export const DEFAULT_APPEARANCE: WorkspaceAppearance = {
    colorMode: "system",
    accentTheme: "indigo",
};

const STORAGE_PREFIX = "kardoe-os:appearance:";
const LAST_APPEARANCE_KEY = `${STORAGE_PREFIX}last`;

function isColorMode(value: unknown): value is AppearanceColorMode {
    return typeof value === "string" && (APPEARANCE_MODES as readonly string[]).includes(value);
}

function isAccent(value: unknown): value is AppearanceAccent {
    return typeof value === "string" && (APPEARANCE_ACCENTS as readonly string[]).includes(value);
}

export function normalizeAppearance(value: unknown): WorkspaceAppearance {
    if (!value || typeof value !== "object") return DEFAULT_APPEARANCE;
    const candidate = value as Partial<WorkspaceAppearance>;
    return {
        colorMode: isColorMode(candidate.colorMode) ? candidate.colorMode : DEFAULT_APPEARANCE.colorMode,
        accentTheme: isAccent(candidate.accentTheme) ? candidate.accentTheme : DEFAULT_APPEARANCE.accentTheme,
    };
}

function readStorage(key: string): WorkspaceAppearance | null {
    try {
        const raw = localStorage.getItem(key);
        return raw === null ? null : normalizeAppearance(JSON.parse(raw));
    } catch {
        return null;
    }
}

export function getCachedAppearance(organizationId?: string | null): WorkspaceAppearance {
    if (organizationId) {
        const organizationAppearance = readStorage(`${STORAGE_PREFIX}${organizationId}`);
        if (organizationAppearance) return organizationAppearance;
    }
    return readStorage(LAST_APPEARANCE_KEY) ?? DEFAULT_APPEARANCE;
}

export function cacheAppearance(organizationId: string | null | undefined, appearance: WorkspaceAppearance): void {
    const normalized = normalizeAppearance(appearance);
    try {
        const value = JSON.stringify(normalized);
        localStorage.setItem(LAST_APPEARANCE_KEY, value);
        if (organizationId) localStorage.setItem(`${STORAGE_PREFIX}${organizationId}`, value);
    } catch {
        // Private browsing or a full storage quota must not block theming.
    }
}

export function applyAppearance(appearance: WorkspaceAppearance): void {
    if (typeof document === "undefined") return;
    const normalized = normalizeAppearance(appearance);
    const root = document.documentElement;
    root.dataset.accent = normalized.accentTheme;
    if (normalized.colorMode === "system") delete root.dataset.theme;
    else root.dataset.theme = normalized.colorMode;

    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (meta) {
        const accent = getComputedStyle(root).getPropertyValue("--color-accent").trim();
        if (accent) meta.content = accent;
    }
}
